import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/common/LanguageSelector';
import { 
  User, 
  Edit,
  Save,
  Building,
  MapPin,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '@/config/api';

const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface BasicInfoTabProps {
  data: any;
  loading: boolean;
  isEditable: boolean;
  editMode: boolean;
  onEdit: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  data,
  loading,
  isEditable,
  editMode,
  onEdit,
  onSave,
  onCancel,
}) => {
  const [editData, setEditData] = useState<any>(data);
  const [languages, setLanguages] = useState<any[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [aiRewriteLoading, setAiRewriteLoading] = useState(false);
  const updateEditData = (updater: (prev: any) => any) => setEditData((prev: any) => updater(prev));

  useEffect(() => {
    setEditData(data);
  }, [data]);

  useEffect(() => {
    if (isEditable) {
      loadUserLanguages();
    }
  }, [isEditable]);

  const loadUserLanguages = async () => {
    try {
      setLanguagesLoading(true);
      const headers = getApiHeaders(true);
      const url = buildApiUrl('profile-tabs/me/profile/languages');
      
      console.log('🔍 Loading user languages from:', url);
      
      const response = await fetch(url, { headers });
      const result = await response.json();
      
      console.log('🔍 User languages API response:', result);
      
      if (result.success && result.data) {
        setLanguages(result.data.languages || []);
        console.log('✅ Loaded user languages:', result.data.languages?.length || 0);
      } else {
        console.error('❌ Failed to load user languages:', result);
      }
    } catch (error) {
      console.error('❌ Failed to load user languages:', error);
    } finally {
      setLanguagesLoading(false);
    }
  };

  const saveUserLanguages = async (newLanguages: any[]) => {
    try {
      const headers = getApiHeaders(true);
      const url = buildApiUrl('profile-tabs/me/profile/languages');
      
      console.log('🔍 Saving user languages to:', url);
      console.log('🔍 Languages data:', newLanguages);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ languages: newLanguages })
      });
      
      const result = await response.json();
      
      console.log('🔍 Save languages response:', result);
      
      if (result.success) {
        setLanguages(newLanguages);
        toast.success('Languages updated successfully');
        console.log('✅ Languages updated successfully');
      } else {
        toast.error('Failed to update languages');
        console.error('❌ Failed to update languages:', result);
      }
    } catch (error) {
      console.error('❌ Failed to save user languages:', error);
      toast.error('Failed to update languages');
    }
  };

  const handleAiRewrite = async () => {
    const currentSummary = editMode ? editData?.summary : data?.summary;
    
    if (!currentSummary || !currentSummary.trim()) {
      toast.error('No summary content to rewrite');
      return;
    }

    try {
      setAiRewriteLoading(true);
      const headers = getApiHeaders(true);
      const url = buildApiUrl('ai/rewrite-summary');
      
      console.log('🤖 Calling AI rewrite API for summary:', currentSummary);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: currentSummary,
          type: 'professional_summary'
        })
      });
      
      const result = await response.json();
      
      console.log('🤖 AI rewrite response:', result);
      
      if (result.success && result.data?.rewritten_content) {
        const rewrittenContent = result.data.rewritten_content;
        
        if (editMode) {
          updateEditData(prev => ({ ...prev, summary: rewrittenContent }));
        } else {
          // If not in edit mode, enter edit mode and set the rewritten content
          onEdit();
          setTimeout(() => {
            updateEditData(prev => ({ ...prev, summary: rewrittenContent }));
          }, 100);
        }
        
        toast.success('Summary rewritten professionally using AI');
      } else {
        toast.error(result.message || 'Failed to rewrite summary');
      }
    } catch (error) {
      console.error('❌ Failed to rewrite summary:', error);
      toast.error('Failed to rewrite summary');
    } finally {
      setAiRewriteLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleSave = () => {
    onSave(editData);
  };

  const handleCancel = () => {
    setEditData(data);
    onCancel();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Basic Information
        </CardTitle>
        {isEditable && !editMode && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {data ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">Current Title</label>
              {editMode ? (
                <input
                  type="text"
                  value={editData?.current_title || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, current_title: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Current job title"
                />
              ) : (
                <p className="text-lg">{data.current_title || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Current Company</label>
              {editMode ? (
                <input
                  type="text"
                  value={editData?.current_company || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, current_company: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Current company"
                />
              ) : (
                <p className="text-lg flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  {data.current_company || 'Not provided'}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Location</label>
              {editMode ? (
                <input
                  type="text"
                  value={editData?.current_location || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, current_location: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Current location"
                />
              ) : (
                <p className="text-lg flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {data.current_location || 'Not provided'}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Job Looking For</label>
              {editMode ? (
                <input
                  type="text"
                  value={editData?.job_looking_for || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, job_looking_for: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Full-time Software Engineer, Part-time Designer"
                />
              ) : (
                <p className="text-lg">{data.job_looking_for || 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">LinkedIn URL</label>
              {editMode ? (
                <input
                  type="url"
                  value={editData?.linkedin_url || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://linkedin.com/in/your-profile"
                />
              ) : (
                data.linkedin_url ? (
                  <p className="text-lg mt-1">
                    <a 
                      href={data.linkedin_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      LinkedIn Profile
                    </a>
                  </p>
                ) : (
                  <p className="text-lg mt-1 text-gray-500">Not provided</p>
                )
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">GitHub URL</label>
              {editMode ? (
                <input
                  type="url"
                  value={editData?.github_url || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, github_url: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://github.com/your-username"
                />
              ) : (
                data.github_url ? (
                  <p className="text-lg mt-1">
                    <a 
                      href={data.github_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      GitHub Profile
                    </a>
                  </p>
                ) : (
                  <p className="text-lg mt-1 text-gray-500">Not provided</p>
                )
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Portfolio URL</label>
              {editMode ? (
                <input
                  type="url"
                  value={editData?.portfolio_url || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, portfolio_url: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://yourportfolio.com"
                />
              ) : (
                data.portfolio_url ? (
                  <p className="text-lg mt-1">
                    <a 
                      href={data.portfolio_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Portfolio Website
                    </a>
                  </p>
                ) : (
                  <p className="text-lg mt-1 text-gray-500">Not provided</p>
                )
              )}
            </div>
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-1">
                <label className="text-sm font-medium text-gray-500">Summary</label>
                {isEditable && (data?.summary || editData?.summary) && (
                  <button
                    onClick={handleAiRewrite}
                    disabled={aiRewriteLoading}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md hover:from-purple-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    title="Rewrite summary professionally using AI"
                  >
                    <Sparkles className={`w-3 h-3 ${aiRewriteLoading ? 'animate-spin' : ''}`} />
                    {aiRewriteLoading ? 'Rewriting...' : 'AI Rewrite'}
                  </button>
                )}
              </div>
              {editMode ? (
                <textarea
                  value={editData?.summary || ''}
                  onChange={(e) => updateEditData(prev => ({ ...prev, summary: e.target.value }))}
                  rows={4}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Professional summary"
                />
              ) : (
                <p className="text-lg mt-1">{data.summary || 'No summary provided'}</p>
              )}
            </div>
            {isEditable && (
              <div className="md:col-span-2 lg:col-span-3">
                <LanguageSelector
                  value={languages}
                  onChange={saveUserLanguages}
                  disabled={languagesLoading}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500">No basic information available</p>
        )}
        
        {editMode && isEditable && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BasicInfoTab;